import type { StaffCreateInput, StaffPatch } from '@veem/crm-schema'
import type { Role } from '@veem/schema'
import { ApiError, api, reportRefreshFailure } from '../../lib/api.ts'
import { copySnapshot, withSnapshotHydration } from '../../lib/snapshot-value.ts'
import { hydrateData } from '../../prototype/data/hydration.ts'
import { PEOPLE } from '../../prototype/data/people.ts'
import { type ProtoState, proto } from '../../prototype/state/store.ts'

interface StaffResult {
  id: string
  password?: string
}
export const staffVersionOf = (id: string) => {
  const person = PEOPLE.find((candidate) => candidate.id === id)
  return (person as typeof person & { version?: number })?.version ?? 0
}
const refresh = async () => {
  const snapshot = await api<{ data: Record<string, unknown>; state: Partial<ProtoState> }>(
    '/snapshot',
  )
  withSnapshotHydration(() => {
    const incoming = copySnapshot(snapshot)
    hydrateData(incoming.data)
    proto.hydrate(incoming.state)
  })
}
const write = async <T>(path: string, body: unknown, method = 'POST') => {
  let result: T
  try {
    result = await api<T>(path, { method, body: JSON.stringify(body) })
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      try {
        await refresh()
      } catch {
        /* Preserve the server conflict message. */
      }
    }
    throw error
  }
  try {
    await refresh()
  } catch {
    reportRefreshFailure()
  }
  return result
}
const command = <T>(
  id: string,
  action: string,
  extra: Record<string, unknown> = {},
  expectedVersion?: number,
) =>
  write<T>(`/users/${encodeURIComponent(id)}/${action}`, {
    version: expectedVersion ?? staffVersionOf(id),
    ...extra,
  })

export const createStaff = async (input: StaffCreateInput) => {
  const result = await write<StaffResult & { password: string }>('/users', input)
  return { id: result.id, password: result.password }
}
export const updateStaff = (id: string, patch: StaffPatch, expectedVersion?: number) =>
  write(
    `/users/${encodeURIComponent(id)}`,
    { version: expectedVersion ?? staffVersionOf(id), patch },
    'PATCH',
  )
export const changeStaffRole = (id: string, role: Role, expectedVersion?: number) =>
  command(id, 'role', { role }, expectedVersion)
export const disableStaff = (
  id: string,
  handoverToId: string | null,
  reason: string,
  expectedVersion?: number,
) => command(id, 'disable', { handoverToId, reason }, expectedVersion)
export const enableStaff = async (id: string, expectedVersion?: number) =>
  (await command<StaffResult>(id, 'enable', {}, expectedVersion)).password
export const resetStaffPassword = async (id: string, expectedVersion?: number) =>
  (await command<StaffResult & { password: string }>(id, 'reset-password', {}, expectedVersion))
    .password
export const endStaffSessions = (id: string, expectedVersion?: number) =>
  command(id, 'end-sessions', {}, expectedVersion)
